import { useEffect, useMemo } from 'react';
import { UseInfiniteQueryResult } from 'react-query';
import { logError } from 'utils/datadog';
import { PaginatedResponse } from '../types';

export const usePaginatedQuery = <T extends PaginatedResponse>(
  infiniteQuery: UseInfiniteQueryResult<T>,
  entityName: string
) => {
  const { hasNextPage, isFetchingNextPage, isError, fetchNextPage } =
    infiniteQuery;

  useEffect(() => {
    // The isError gate stops the auto-fetch after a failed page: a failure leaves
    // hasNextPage true, so without it this effect refires the same failing request
    // (and its retries) in an unbounded loop for as long as the tab lives.
    if (hasNextPage && !isFetchingNextPage && !isError) {
      fetchNextPage().catch((error: unknown) => {
        logError(`Failed to fetch next page for ${entityName}`, {
          cause: error
        });
      });
    }
  }, [hasNextPage, isFetchingNextPage, isError, fetchNextPage, entityName]);

  const data = useMemo(() => {
    return infiniteQuery.data?.pages?.flatMap(page => page?.data || []) || [];
  }, [infiniteQuery.data]);

  return {
    data,
    isLoading: infiniteQuery.isLoading || infiniteQuery.isFetchingNextPage,
    error: infiniteQuery.error,
    isError: infiniteQuery.isError
  };
};
